import { Button, Input, Table } from '@/components/commons';
import { APP_ROUTER } from '@/routes/routes';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { Badge, Form, Image, Modal, Radio, Select, Spin, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { ImageService } from '@/services/image';
import { CompanyService } from '@/services/company';
import { ENDPOINT } from '@/services/endpoint';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import { useQuery } from 'react-query';
import { IUpdateCompanyRequest } from '@/types/request/company';
import { IResponseError } from '@/types/common';
import type { IInviteCodeRow } from '../CompanyCreate/types';
import { toYYYYMMDD } from '../CompanyCreate/inviteCodeDto';
import { CsvUploadModal } from '../CompanyCreate/CsvUploadModal';
import type { IValidatedErrorRow } from '../CompanyCreate/types';
import { validateInviteCodeUpdateRows } from '../CompanyCreate/validateInviteCodes';
import { ERROR, ERROR_MESSAGE, SUCCESS_MESSAGE } from '@/utils/constants/messages';

interface IProps {
  title?: string;
}

export const CompanyDetail: React.FC<IProps> = ({ title }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [isShowService, setIsShowService] = useState<boolean>(false);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [successRows, setSuccessRows] = useState<IInviteCodeRow[]>([]);
  const [errorRows, setErrorRows] = useState<IValidatedErrorRow[]>([]);
  const [inviteCodeUpdateData, setInviteCodeUpdateData] = useState<
    IUpdateCompanyRequest['companyInviteCodes']
  >([]);

  const { data: detailRes, isFetching: loadingDetail } = useQuery(
    [ENDPOINT.COMPANY.GET_DETAIL.replace(':id', id ?? '')],
    () => CompanyService.getDetail(Number(id)),
    {
      enabled: !!id, onError: (response: IResponseError) => {
        if (response.message === ERROR.COMPANY_NOT_FOUND) {
          navigate(APP_ROUTER.COMPANY.LIST);
          notificationMessage({
            message: ERROR_MESSAGE[response.message as keyof typeof ERROR_MESSAGE],
            type: NOTIFY_STATUS.ERROR,
          });
        }
      }
    },
  );

  const {
    data: articleSelectRes,
    isFetching: loadingArticles,
    refetch: refetchArticles,
  } = useQuery(
    [ENDPOINT.COMPANY.GET_ARTICLES, id],
    () => CompanyService.getArticles(Number(id)),
    { enabled: !!detailRes?.data },
  );

  const detail = detailRes?.data;
  const articleOptions =
    articleSelectRes?.data?.map((a) => ({
      value: a.id,
      label: a.name,
    })) ?? [];

  useEffect(() => {
    if (!detail) return;
    const showService = !!detail.isShowService;
    setIsShowService(showService);
    setBannerPreview(detail.image?.url ?? null);
    const articleIds = Array.isArray(detail.articles)
      ? detail.articles.map((a: any) =>
        typeof a === 'number' ? a : (a as { id: number }).id,
      )
      : [];
    form.setFieldsValue({
      companyName: detail.name,
      isShowService: showService,
      serviceName: detail.serviceName ?? '',
      relatedArticleIds: articleIds,
    });
  }, [detail, form]);

  const inviteCodeColumns: ColumnsType<IInviteCodeRow> = [
    { title: 'ID', dataIndex: 'sortOrder', key: 'sortOrder' },
    { title: 'コード', dataIndex: 'code', key: 'code' },
    { title: '有効開始日', dataIndex: 'validFrom', key: 'validFrom', render: (text: string) => toYYYYMMDD(text) },
    { title: '有効終了日', dataIndex: 'validTo', key: 'validTo', render: (text: string) => toYYYYMMDD(text) },
    { title: 'CogEvo 有効フラグ', dataIndex: 'cogEvo', key: 'cogEvo' },
    { title: 'のうKNOW 有効フラグ', dataIndex: 'nouknow', key: 'nouknow' },
    { title: '有効上限数', dataIndex: 'quantity', key: 'quantity' },
  ];

  type ErrorRowWithKey = IValidatedErrorRow & { _key: number };
  const errorColumns: ColumnsType<ErrorRowWithKey> = [
    {
      title: 'ID',
      key: 'id',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.id.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.id.value,
    },
    {
      title: 'コード',
      key: 'code',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.code.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.code.value,
    },
    {
      title: '有効開始日',
      key: 'valid_from',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.valid_from.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.valid_from.value,
    },
    {
      title: '有効終了日',
      key: 'valid_to',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.valid_to.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.valid_to.value,
    },
    {
      title: 'CogEvo 有効フラグ',
      key: 'cog_evo',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.cog_evo.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.cog_evo.value,
    },
    {
      title: 'のうKNOW 有効フラグ',
      key: 'nounow',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.nounow.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.nounow.value,
    },
    {
      title: '有効上限数',
      key: 'limit',
      onCell: (record: ErrorRowWithKey) => ({
        style: { backgroundColor: record.limit.color ?? undefined },
      }),
      render: (_: unknown, record: ErrorRowWithKey) => record.limit.value,
    },
  ];

  const handleCsvRegister = async (rows: IInviteCodeRow[]) => {
    const codes = rows
      .map((r) => String(r.code ?? '').trim())
      .filter((c) => c !== '');

    let conflictingCodes: string[] = [];
    try {
      const res = await CompanyService.checkInviteCode({ codes, companyId: Number(id) });
      conflictingCodes = res?.data?.conflictingCodes ?? [];
    } catch (e) {
      console.error(e);
      notificationMessage({
        message: 'コードの確認に失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    }

    const { errors, success, updateData } = validateInviteCodeUpdateRows(
      rows,
      conflictingCodes,
      detail?.inviteCodes ?? [],
    );
    // Re-upload should replace both success and error data
    const successWithRowId = success.map((r, i) => ({ ...r, id: i + 1 }));
    setSuccessRows(successWithRowId);
    setErrorRows(errors);
    setInviteCodeUpdateData(updateData);
  };

  if (id && !detail && !loadingDetail) {
    return (
      <div className='p-post-list p-company-create'>
        <div className='flex justify-center items-center min-h-[200px]'>
          データがありません
        </div>
      </div>
    );
  }

  return (
    <div className='p-post-list p-company-create'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[24px] mx-auto w-full max-w-[1280px] pb-10'>

        <div className='flex items-center justify-between gap-3'>
          <div className='text-[20px] font-bold'>団体情報の編集</div>
          <Button
            type='primary'
            className='min-w-[180px] !bg-[#2F2F2F] !border-[#2F2F2F] !text-white'
            onClick={() => setStopModalOpen(true)}
            loading={changingStatus}
          >
            {detail?.status ? 'この団体の利用を停止する' : 'この団体の利用を再開する'}
          </Button>
        </div>

        <Modal
          open={stopModalOpen}
          onCancel={() => setStopModalOpen(false)}
          footer={null}
          centered
          style={{ maxWidth: 440 }}
        >
          <div className='pt-2'>
            <div className='text-[18px] font-semibold mb-6'>
              {detail?.status ? '以下の団体コードを停止しますか？' : '以下の団体コードを再開しますか？'}
            </div>
            <div className='bg-[#F7F7F7] rounded-[8px] px-4 py-3 mb-5'>
              <div className='text-[#8C8C8C] text-sm mb-2'>団体名</div>
              <div className='text-[18px] font-semibold'>{detail?.name ?? ''}</div>
            </div>
            <div className='text-[#666] leading-8 mb-8'>
              {
                detail?.status ? (
                  <>
                    <div>・停止すると、コードを使用中のユーザーが有効サービスを利用できなくなります。</div>
                    <div>・利用を再開するボタンを押すと、再開できます。</div>
                  </>
                ) :
                  (<div>・再開すると、再度コードごとの有効ステータスを参照し、サービスが有効化されます。</div>)
              }
            </div>
            <div className='flex justify-end gap-3'>
              <Button
                className='min-w-[120px]'
                onClick={() => setStopModalOpen(false)}
                disabled={changingStatus}
              >
                キャンセル
              </Button>
              <Button
                type='primary'
                className='min-w-[120px] !bg-[#2F2F2F] !border-[#2F2F2F] !text-white'
                loading={changingStatus}
                onClick={async () => {
                  setStopModalOpen(false);
                  setChangingStatus(true);
                  try {
                    await CompanyService.changeStatus(Number(id));
                    notificationMessage({
                      message: SUCCESS_MESSAGE.UPDATE_COMPANY,
                      type: NOTIFY_STATUS.SUCCESS,
                    });
                    navigate(APP_ROUTER.COMPANY.LIST);
                  } catch (error) {
                    const err = error as IResponseError;
                    const message =
                      typeof err?.message === 'string'
                        ? err.message
                        : '停止に失敗しました。';
                    notificationMessage({
                      message,
                      type: NOTIFY_STATUS.ERROR,
                    });
                  } finally {
                    setChangingStatus(false);
                  }
                }}
              >
                 {detail?.status ? '停止する' : '再開する'}
              </Button>
            </div>
          </div>
        </Modal>

        <Spin spinning={loadingDetail}>
          <Form
            layout='vertical'
            className='flex flex-col gap-8'
            form={form}
            validateTrigger='onSubmit'
            onFinish={async (values) => {
              if (errorRows.length > 0) return;
              const {
                companyName,
                bannerImage,
                relatedArticleIds,
                serviceName,
                isShowService: showService,
              } = values as any;

              const body: IUpdateCompanyRequest = {
                name: (companyName ?? '').trim(),
                isShowService: !!showService,
                status: 1,
              };

              body.companyInviteCodes = inviteCodeUpdateData

              if (showService) {
                body.serviceName = (serviceName ?? '').trim();
                body.articles = (relatedArticleIds || []).map((idVal: any) =>
                  Number(idVal),
                );
                if (bannerImage instanceof File) {
                  try {
                    const response =
                      await ImageService.uploadFile(bannerImage);
                    body.imageUrl = response?.data?.url ?? '';
                  } catch (error) {
                    console.error('Upload banner image failed', error);
                    notificationMessage({
                      message: 'バナー画像のアップロードに失敗しました。',
                      type: NOTIFY_STATUS.ERROR,
                    });
                    return;
                  }
                }
              }

              setSubmitting(true);
              try {
                await CompanyService.update(Number(id), body);
                notificationMessage({
                  message: SUCCESS_MESSAGE.UPDATE_COMPANY,
                  type: NOTIFY_STATUS.SUCCESS,
                });
                navigate(APP_ROUTER.COMPANY.LIST);
              } catch (error) {
                const err = error as IResponseError;
                if (err?.message === 'EXIST_COMPANY_NAME') {
                  form.setFields([
                    {
                      name: 'companyName',
                      errors: ['団体名は既に存在します。'],
                    },
                  ]);
                  return;
                }
                if (err?.message === ERROR.ARTICLE_ASSIGNED_TO_OTHER_COMPANY) {
                  const detailArticles = detail?.articles;
                  const articleIds = Array.isArray(detailArticles)
                    ? detailArticles.map((a: any) =>
                      typeof a === 'number' ? a : (a as { id: number }).id,
                    )
                    : [];
                  notificationMessage({
                    message: ERROR_MESSAGE.ARTICLE_ASSIGNED_TO_OTHER_COMPANY,
                    type: NOTIFY_STATUS.ERROR,
                  });
                  form.setFieldsValue({ relatedArticleIds: articleIds });
                  await refetchArticles();
                  return;
                }
                const message =
                  typeof err?.message === 'string'
                    ? err.message
                    : '更新に失敗しました。';
                notificationMessage({
                  message,
                  type: NOTIFY_STATUS.ERROR,
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            <section className='bg-[white] rounded-[8px] px-6 py-5'>
              <Form.Item
                label={
                  <span className='flex items-center gap-2 font-bold'>
                    団体名
                    <Badge status='error' />
                  </span>
                }
                name='companyName'
                validateTrigger={['onChange', 'onSubmit']}
                rules={[
                  {
                    validator: (_, value) => {
                      const v =
                        typeof value === 'string' ? value.trim() : '';
                      if (!v) {
                        return Promise.reject(
                          new Error('団体名は必須です。'),
                        );
                      }
                      if (v.length > 50) {
                        return Promise.reject(
                          new Error(
                            '団体名は50文字以内で入力してください。',
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder='例：〇〇株式会社'
                  maxLength={50}
                  onBlur={(e) => {
                    const raw = e.target.value ?? '';
                    const trimmed = raw.trim();
                    if (trimmed !== raw) {
                      form.setFieldValue('companyName', trimmed);
                    }
                  }}
                />
              </Form.Item>
            </section>

            {/* 解放コンテンツの設定 */}
            <section className='bg-[white] rounded-[8px]'>
              <div className=' font-semibold text-[16px] w-full border-b border-gray-200 px-6 py-4'>
                解放コンテンツの設定
              </div>
              <Form.Item
                label={
                  <span className='flex items-center gap-2 font-bold'>
                    解放コンテンツの有無
                    <Badge status='error' />
                  </span>
                }
                name='isShowService'
                initialValue={false}
                className='px-6 pt-4'
              >
                <Radio.Group
                  value={isShowService}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIsShowService(value);
                    form.setFieldsValue({ isShowService: value });
                  }}
                >
                  <Radio value={true}>あり</Radio>
                  <Radio value={false}>なし</Radio>
                </Radio.Group>
              </Form.Item>

              {isShowService && (
                <div className='mt-6 flex flex-col gap-4'>
                  <Form.Item
                    className='px-6'
                    label={
                      <span className='flex items-center gap-2 font-bold'>
                        サービス名
                        <Badge status='error' />
                      </span>
                    }
                    name='serviceName'
                    validateTrigger={['onChange', 'onSubmit']}
                    rules={[
                      {
                        validator: (_, value) => {
                          const v =
                            typeof value === 'string' ? value.trim() : '';
                          if (!v) {
                            return Promise.reject(
                              new Error('サービス名は必須です。'),
                            );
                          }
                          if (v.length > 30) {
                            return Promise.reject(
                              new Error(
                                'サービス名は30文字以内で入力してください。',
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder='例：〇〇株式会社'
                      maxLength={30}
                      onBlur={(e) => {
                        const raw = e.target.value ?? '';
                        const trimmed = raw.trim();
                        if (trimmed !== raw) {
                          form.setFieldValue('serviceName', trimmed);
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    className='px-6'
                    label={
                      <span className='flex items-center gap-2 font-bold'>
                        バナー画像
                        <Badge status='error' />
                      </span>
                    }
                    name='bannerImage'
                    rules={
                      isShowService
                        ? [
                          {
                            validator: (_, value) => {
                              if (
                                value instanceof File ||
                                bannerPreview
                              ) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error(
                                  'バナー画像をアップロードしてください。',
                                ),
                              );
                            },
                          },
                        ]
                        : []
                    }
                  >
                    <>
                      <div className='flex items-baseline gap-2 pt-2'>
                        {!bannerPreview ? (
                          <Upload
                            maxCount={1}
                            listType='text'
                            showUploadList={false}
                            customRequest={() => { }}
                            beforeUpload={(file: File) => {
                              const fileExtension = file.name
                                .split('.')
                                .pop()
                                ?.toLowerCase();

                              if (file.size > FILE_SIZE_LIMIT) {
                                form.setFields([
                                  {
                                    name: 'bannerImage',
                                    errors: [
                                      '画像のサイズは10MBを超えています。',
                                    ],
                                  },
                                ]);
                                return Upload.LIST_IGNORE;
                              }

                              if (
                                !SUPPORTED_EXTENSION.includes(
                                  fileExtension || '',
                                ) ||
                                !SUPPORTED_FORMATS.includes(file.type)
                              ) {
                                form.setFields([
                                  {
                                    name: 'bannerImage',
                                    errors: [
                                      '画像は .jpg、.png、.jpeg 形式ではありません。',
                                    ],
                                  },
                                ]);
                                return Upload.LIST_IGNORE;
                              }

                              const reader = new FileReader();
                              reader.onload = () => {
                                setBannerPreview(reader.result as string);
                                form.setFieldsValue({ bannerImage: file });
                                form.setFields([
                                  { name: 'bannerImage', errors: [] },
                                ]);
                              };
                              reader.readAsDataURL(file);

                              return Upload.LIST_IGNORE;
                            }}
                          >
                            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center bg-white cursor-pointer'>
                              <Button
                                type='default'
                                className='text-[#84ab42] border border-[#84ab42] font-semibold'
                                icon={<UploadOutlined />}
                              >
                                画像を選択
                              </Button>
                            </div>
                          </Upload>
                        ) : (
                          <div className='relative inline-block'>
                            <Image
                              height={100}
                              src={bannerPreview || FALLBACK_URL}
                              className='block rounded-lg'
                              style={{ width: 'auto', maxWidth: 'none' }}
                              fallback={FALLBACK_URL}
                            />
                            <Button
                              type='primary'
                              shape='circle'
                              icon={<CloseOutlined />}
                              size='small'
                              onClick={() => {
                                setBannerPreview(null);
                                form.setFieldsValue({
                                  bannerImage: undefined,
                                });
                              }}
                              className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                            />
                          </div>
                        )}
                      </div>
                      <p className='title-calendar font-medium text-xs mt-2'>
                        <span className='font-bold mr-1'>※</span>
                        1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
                      </p>
                    </>
                  </Form.Item>

                  <Form.Item
                    className='px-6'
                    label={
                      <span className='flex items-center gap-2 font-bold'>
                        限定記事の選択
                      </span>
                    }
                    name='relatedArticleIds'
                  >
                    <Select
                      mode='multiple'
                      placeholder='紐づける記事を検索...'
                      style={{ width: '100%' }}
                      options={articleOptions}
                      loading={loadingArticles}
                      filterOption={(input, option) =>
                        String(
                          (option as any)?.label ??
                          (option as any)?.children ??
                          '',
                        )
                          .toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    />
                  </Form.Item>
                </div>
              )}
            </section>

            {/* 団体コードの追加 */}
            <section className='rounded-[8px] py-5 flex flex-col gap-4'>
              <div className='font-bold text-[20px]'>団体コードの追加</div>
              {
                successRows.length + errorRows.length > 0 && (
                  <div className='text-[20px] font-bold'>
                    結果：全{successRows.length + errorRows.length}件
                    {successRows.length > 0 && ` 成功${successRows.length}件`}
                    {errorRows.length > 0 && ` 失敗${errorRows.length}件`}
                  </div>
                )
              }
              <div>
                <Button type='primary' onClick={() => setCsvModalOpen(true)}>
                  + CSVのアップロード
                </Button>
              </div>

              <CsvUploadModal
                open={csvModalOpen}
                onClose={() => setCsvModalOpen(false)}
                onRegister={handleCsvRegister}
              />

              {!!errorRows.length && (
                <Table
                  className={
                    errorRows.length > 0
                      ? 'company-create-error-table company-create-error-table--with-title'
                      : 'company-create-error-table'
                  }
                  rowKey='_key'
                  bordered
                  columns={errorColumns}
                  dataSource={errorRows.map((row, i) => ({ ...row, _key: i }))}
                  title={() => (
                    <div className='font-semibold'>
                      登録に失敗したコード
                    </div>
                  )}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                    showTotal: (total, [start, end]) =>
                      `${start}~${end}件表示 / 全${total}行`,
                    position: ['bottomRight'],
                    className: 'error-table-pagination',
                  }}
                />
              )}
              {
                (!errorRows.length && !successRows.length) && (
                  <Table
                    rowKey='id'
                    bordered
                    columns={inviteCodeColumns}
                    dataSource={detail?.inviteCodes ?? []}
                    {...(!!errorRows.length && {
                      title: () => (
                        <div className='font-bold'>
                          登録に失敗したコード
                        </div>
                      ),
                    })}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: false,
                      showTotal: (total, [start, end]) =>
                        `${start}~${end}件表示 / 全${total}行`,
                      position: ['bottomRight'],
                      className: 'error-table-pagination',
                    }}
                    locale={{
                      emptyText: (
                        <div className='py-10 text-center text-[#666] text-sm leading-6'>
                          <div className='mb-4 font-semibold'>データがありません</div>
                          <div className='inline-block text-left'>
                            <div>以下の要件でCSVを作成してください。</div>
                            <div>・id：通し番号</div>
                            <div>・code：メンバーに配布するコード（共通：ユニークコード）</div>
                            <div>・valid_from：有効開始日（YYYY-MM-DD）</div>
                            <div>・valid_to：有効終了日（YYYY-MM-DD）</div>
                            <div>・cog_evo：1=ON / 0=OFF</div>
                            <div>・nounow：1=ON / 0=OFF</div>
                            <div>・limit：使用上限数</div>
                          </div>
                        </div>
                      ),
                    }}
                  />
                )
              }
            </section>

            <div className='flex justify-center gap-4 pt-4'>
              <Button
                className='min-w-[120px]'
                onClick={() => navigate(APP_ROUTER.COMPANY.LIST)}
              >
                キャンセル
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                className='min-w-[120px]'
                loading={submitting}
                disabled={errorRows.length > 0}
              >
                保存
              </Button>
            </div>
          </Form>
        </Spin>
      </div>
    </div>
  );
};
